// TeamAdminPage — /hub/admin/team (Epic 3 — Team reporting-line editor).
//
// An admin (Brian, Aly — can('admin.manage')) can edit the reporting line: set a
// manager (primary or additional), remove a manager, and set someone to top-of-tree.
// Everyone else sees the tree read-only (controls HIDDEN, not disabled-with-padlock).
// The function refuses cycles and self-management server-side, so the UI does NOT
// check for either — it surfaces the refusal message verbatim (knowledge-base §5).
//
// Reads: fn_team_load (the tree) + getAssignableMembers (the filtered member
// picker — current humans only, reusing Epic 1's helper). Writes: fn_team_set_manager
// / fn_team_remove_manager (never retried; optimistic + revert on error).
//
// Gated by admin.manage on the page (the capability check is a convenience; the
// wall is the database — every function resolves auth.uid() itself). A viewer
// without admin.manage sees the tree but no edit controls; <NotPermitted> renders
// only if the READ itself gates (the viewer is not a current ops_team_members row).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HubShell } from "../../components/hub/shell";
import { NotPermitted } from "../../components/hub/kit";
import { EmptyState, ErrorState } from "../../components/shared/StateMessage";
import PanelErrorBoundary from "../../components/shared/PanelErrorBoundary";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { getTeamLoad, setTeamManager, removeTeamManager } from "../../api/hub/team";
import { getAssignableMembers } from "../../api/conversations";

export default function TeamAdminPage() {
  const { can } = useWorkspace();
  const permitted = can("admin.manage");

  const [status, setStatus] = useState("loading"); // loading | ready | error | gated
  const [teamData, setTeamData] = useState(null);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    setError(null);
    setSaveError(null);
    try {
      const [res, mems] = await Promise.all([getTeamLoad(), getAssignableMembers()]);
      setTeamData(res);
      setMembers(Array.isArray(mems) ? mems : []);
      setStatus("ready");
    } catch (err) {
      // A gate refusal (the viewer is not a current ops_team_members row) →
      // <NotPermitted>, not a crash. The read itself gates on that.
      const msg = err?.message || "Couldn't load the team.";
      if (/not permitted|not authorised|permission denied|forbidden/i.test(msg)) {
        setStatus("gated");
      } else {
        setError(msg);
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const team = teamData?.team || [];
  const nameById = useMemo(() => {
    const m = {};
    for (const p of team) m[p.id] = p.name;
    return m;
  }, [team]);

  const { roots, childrenByParent } = useMemo(() => {
    const byParent = {};
    const r = [];
    for (const p of team) {
      const mgr = p.reports_to_id;
      if (!mgr) {
        r.push(p);
      } else {
        if (!byParent[mgr]) byParent[mgr] = [];
        byParent[mgr].push(p);
      }
    }
    return { roots: r, childrenByParent: byParent };
  }, [team]);

  const selected = selectedId ? team.find((p) => p.id === selectedId) : null;

  const handleSetManager = useCallback(
    async (memberId, managerId, isPrimary) => {
      setSaving(true);
      setSaveError(null);
      try {
        await setTeamManager({ memberId, managerId, isPrimary });
        await load();
      } catch (err) {
        // Gate message (cycle / self-management refusal) — surface verbatim.
        setSaveError(err?.message || "Couldn't set the manager.");
      } finally {
        setSaving(false);
      }
    },
    [load],
  );

  const handleRemoveManager = useCallback(
    async (memberId, managerId) => {
      setSaving(true);
      setSaveError(null);
      try {
        await removeTeamManager({ memberId, managerId });
        await load();
      } catch (err) {
        setSaveError(err?.message || "Couldn't remove the manager.");
      } finally {
        setSaving(false);
      }
    },
    [load],
  );

  if (status === "gated") {
    return (
      <HubShell>
        <div className="p-6">
          <NotPermitted
            title="Not permitted"
            message="You need to be a current team member to view the reporting line."
          />
        </div>
      </HubShell>
    );
  }

  if (status === "error") {
    return (
      <HubShell>
        <div className="p-6">
          <ErrorState variant="panel" message={error} onRetry={load} />
        </div>
      </HubShell>
    );
  }

  if (status === "loading") {
    return (
      <HubShell>
        <div className="p-6">
          <div className="animate-pulse space-y-3" data-testid="team-loading">
            <div className="h-4 w-48 rounded bg-gray-200 dark:bg-gray-800" />
            <div className="h-4 w-36 rounded bg-gray-200 dark:bg-gray-800" />
            <div className="h-4 w-40 rounded bg-gray-200 dark:bg-gray-800" />
          </div>
        </div>
      </HubShell>
    );
  }

  const managerName = (id) => (id ? nameById[id] || "—" : "Top of the tree");

  return (
    <HubShell>
      <div className="p-6" data-testid="team-admin-page">
        <div className="mb-4">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Reporting line
          </h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {permitted
              ? "Set who reports to whom. The database refuses cycles and self-management."
              : "View-only — you need admin access to edit the reporting line."}
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr,360px]">
          <div
            className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900"
            data-testid="team-tree"
          >
            {roots.length === 0 ? (
              <EmptyState variant="inset" message="Nobody reports to you yet." padding="py-12" />
            ) : (
              <ul className="space-y-1">
                {roots.map((person) => (
                  <TreeNode
                    key={person.id}
                    person={person}
                    childrenByParent={childrenByParent}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    depth={0}
                  />
                ))}
              </ul>
            )}
          </div>


          <div
            className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900"
            data-testid="team-editor-panel"
          >
            {!selected ? (
              <EmptyState
                variant="inset"
                message="Select a person to view or edit their reporting line."
                padding="py-12"
              />
            ) : (
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {selected.name}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Reports to:{" "}
                  <span className="font-medium">
                    {managerName(selected.reports_to_id)}
                  </span>
                </p>

                {permitted && (
                  <div className="mt-4 space-y-3" data-testid="team-editor-controls">
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Primary manager
                      </span>
                      <select
                        className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                        value={selected.reports_to_id || ""}
                        disabled={saving}
                        onChange={(e) => {
                          const val = e.target.value || null;
                          handleSetManager(selected.id, val, true);
                        }}
                        data-testid="team-manager-select"
                      >
                        <option value="">Top of the tree</option>
                        {members
                          .filter((m) => m.id !== selected.id)
                          .map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                      </select>
                    </label>

                    {selected.reports_to_id && (
                      <button
                        type="button"
                        onClick={() => handleSetManager(selected.id, null, true)}
                        disabled={saving}
                        className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                        data-testid="team-set-top"
                      >
                        Set to top of tree
                      </button>
                    )}

                    {selected.reports_to_id && (
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveManager(selected.id, selected.reports_to_id)
                        }
                        disabled={saving}
                        className="block rounded-md border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                        data-testid="team-remove-manager"
                      >
                        Remove manager
                      </button>
                    )}

                    {saving && (
                      <p className="text-xs text-gray-400" data-testid="team-saving">
                        Saving…
                      </p>
                    )}
                  </div>
                )}

                {saveError && (
                  <div className="mt-3" data-testid="team-save-error">
                    <ErrorState variant="strip" message={saveError} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </HubShell>
  );
}

// ── Tree node (recursive) ──────────────────────────────────────────────────────
function TreeNode({ person, childrenByParent, selectedId, onSelect, depth }) {
  const kids = childrenByParent[person.id] || [];
  const isSelected = selectedId === person.id;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(person.id)}
        className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors ${
          isSelected
            ? "bg-[#32B1FF]/10 font-medium text-[#32B1FF]"
            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
        }`}
        style={{ paddingLeft: `${depth * 1.25 + 0.5}rem` }}
        data-testid={`team-node-${person.id}`}
      >
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${
            person.reports_to_id ? "bg-gray-300 dark:bg-gray-600" : "bg-[#32B1FF]"
          }`}
          aria-hidden="true"
        />
        <span>{person.name}</span>
      </button>
      {kids.length > 0 && (
        <ul className="space-y-1">
          {kids.map((kid) => (
            <TreeNode
              key={kid.id}
              person={kid}
              childrenByParent={childrenByParent}
              selectedId={selectedId}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

